import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, useMotionValue, useSpring, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Reveal } from "@/components/Reveal";

/* --- Animated geometric icons (idle motion, intensified on group hover) --- */

function IconInvestigate() {
  return (
    <svg viewBox="0 0 64 64" className="w-16 h-16" aria-hidden="true">
      {[26, 18, 10].map((r, i) => (
        <circle
          key={r}
          cx="32"
          cy="32"
          r={r}
          fill="none"
          stroke="#1E4D38"
          strokeOpacity={0.25 + i * 0.2}
          strokeWidth="0.8"
          strokeDasharray={i === 0 ? "2 5" : "none"}
          style={{
            transformOrigin: "32px 32px",
            animation: `pulse-soft ${4 + i * 1.5}s ease-in-out ${i * 0.7}s infinite`,
          }}
        />
      ))}
      <circle cx="32" cy="32" r="2.4" fill="#1E4D38" />
      <line x1="32" y1="32" x2="48" y2="18" stroke="#0F0F0F" strokeOpacity="0.5" strokeWidth="0.7"
        style={{ transformOrigin: "32px 32px", animation: "spin-slow 14s linear infinite" }} />
      <circle cx="48" cy="18" r="1.6" fill="#0F0F0F"
        style={{ transformOrigin: "32px 32px", animation: "spin-slow 14s linear infinite" }} />
    </svg>
  );
}

function IconTranslate() {
  return (
    <svg viewBox="0 0 64 64" className="w-16 h-16" aria-hidden="true">
      {[16, 32, 48].map((y, i) => (
        <line key={y} x1="10" y1={y} x2="54" y2={y} stroke="#1E4D38" strokeOpacity="0.4"
          strokeWidth="0.8" strokeDasharray="3 6"
          style={{ animation: `dash-move ${6 + i * 2}s linear infinite` }} />
      ))}
      {[[10, 16], [32, 32], [54, 48]].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="2" fill="#0F0F0F"
          style={{ animation: `pulse-soft 3.5s ease-in-out ${i * 0.6}s infinite` }} />
      ))}
      <line x1="10" y1="16" x2="32" y2="32" stroke="#1E4D38" strokeOpacity="0.6" strokeWidth="0.8" />
      <line x1="32" y1="32" x2="54" y2="48" stroke="#1E4D38" strokeOpacity="0.6" strokeWidth="0.8" />
      <circle cx="32" cy="32" r="2.6" fill="#1E4D38" />
    </svg>
  );
}

function IconApply() {
  const pts = Array.from({ length: 6 }, (_, i) => {
    const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
    return [32 + Math.cos(a) * 20, 32 + Math.sin(a) * 20];
  });
  return (
    <svg viewBox="0 0 64 64" className="w-16 h-16 transition-transform duration-700 group-hover:rotate-[30deg]" aria-hidden="true">
      <polygon
        points={pts.map((p) => p.join(",")).join(" ")}
        fill="none"
        stroke="#1E4D38"
        strokeOpacity="0.55"
        strokeWidth="0.9"
      />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={i === 0 ? 2.4 : 1.6} fill={i === 0 ? "#1E4D38" : "#0F0F0F"}
          style={{ animation: `pulse-soft 4s ease-in-out ${i * 0.4}s infinite` }} />
      ))}
      <circle cx="32" cy="32" r="1.8" fill="#1E4D38" />
    </svg>
  );
}

function IconCollaborate() {
  return (
    <svg viewBox="0 0 64 64" className="w-16 h-16" aria-hidden="true">
      <circle cx="24" cy="32" r="15" fill="none" stroke="#1E4D38" strokeOpacity="0.55" strokeWidth="0.9"
        style={{ animation: "drift 6s ease-in-out infinite" }} />
      <circle cx="40" cy="32" r="15" fill="none" stroke="#0F0F0F" strokeOpacity="0.4" strokeWidth="0.9"
        style={{ animation: "drift 6s ease-in-out 3s infinite reverse" }} />
      <circle cx="32" cy="32" r="2.2" fill="#1E4D38"
        style={{ animation: "pulse-soft 3s ease-in-out infinite" }} />
    </svg>
  );
}

const STEPS = [
  {
    n: "01",
    title: "We investigate",
    body: "We explore emerging questions with rigour and openness.",
    Icon: IconInvestigate,
  },
  {
    n: "02",
    title: "We translate",
    body: "We turn complexity into knowledge that can travel.",
    Icon: IconTranslate,
  },
  {
    n: "03",
    title: "We apply",
    body: "We test ideas in practice and learn from real-world contexts.",
    Icon: IconApply,
  },
  {
    n: "04",
    title: "We collaborate",
    body: "We work with partners across sectors to co-create solutions.",
    Icon: IconCollaborate,
  },
];

/* Step with cursor-magnetic icon + scroll-linked parallax/rotation */
function StepCard({ s, i, progress }) {
  const reduced = useReducedMotion();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 130, damping: 15 });
  const sy = useSpring(my, { stiffness: 130, damping: 15 });
  const scrollRot = useTransform(progress, [0, 1], [i % 2 === 0 ? -10 : 10, i % 2 === 0 ? 10 : -10]);
  const scrollDrift = useTransform(progress, [0, 1], [16 + i * 7, -16 - i * 7]);

  const onMove = (e) => {
    if (reduced || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 18);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 14);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} data-testid={`howwework-step-${s.n}`}>
      <p className="text-sm tracking-[0.2em] text-ember font-medium">{s.n}</p>
      <motion.div
        className="mt-6 opacity-80 transition-opacity duration-500 group-hover:opacity-100 will-change-transform"
        style={reduced ? {} : { y: scrollDrift, rotate: scrollRot }}
      >
        <motion.div style={reduced ? {} : { x: sx, y: sy }}>
          <s.Icon />
        </motion.div>
      </motion.div>
      <h3 className="mt-6 text-lg tracking-tight text-ink transition-colors duration-300 group-hover:text-ember">
        {s.title}
      </h3>
      <p className="mt-3 text-sm text-fog leading-relaxed">{s.body}</p>
    </div>
  );
}

export function HowWeWork() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  return (
    <section ref={sectionRef} className="border-t border-line bg-white" data-testid="howwework-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-0">
          {/* Intro */}
          <Reveal className="lg:col-span-3 lg:pr-10">
            <p className="text-xs tracking-[0.25em] uppercase text-ember" data-testid="howwework-eyebrow">
              How we work
            </p>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-6" data-testid="howwework-heading">
              From research to real impact.
            </h2>
            <p className="mt-6 text-sm text-fog leading-relaxed">
              We combine inquiry, collaboration and experimentation to create knowledge that
              translates into meaningful change.
            </p>
            <Link
              to="/practice"
              data-testid="howwework-approach-link"
              className="group inline-flex items-center gap-2 mt-8 text-sm tracking-wide link-editorial"
            >
              Learn more about our approach
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>

          {/* Steps */}
          <div className="lg:col-span-9 grid sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-0">
            {STEPS.map((s, i) => (
              <Reveal
                key={s.n}
                delay={0.1 + i * 0.12}
                className={`group lg:px-8 ${i > 0 ? "lg:border-l lg:border-line" : ""} ${i === 0 ? "lg:pl-10" : ""}`}
              >
                <StepCard s={s} i={i} progress={scrollYProgress} />
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
